import React, { useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import { limitNotif } from "helpers/notificationHelper";
import { IconBox } from "components/IconBox";
import Tooltip from "components/Tooltip";
import { AppMenuLink } from "../AppMenuLink";

interface ExpandableMenuItemProps {
	children: React.ReactNode;
	collapsedLink: string;
	icon: React.ReactNode;
	isCollapsed: boolean;
	label: string;
	storageKey: string;
	badge?: number | null;
	testId?: string;
	tourName?: string;
	onNavigate?: () => void;
}

export const ExpandableMenuItem: React.FC<ExpandableMenuItemProps> = ({
	children,
	collapsedLink,
	icon,
	isCollapsed,
	label,
	storageKey,
	badge,
	testId,
	tourName,
	onNavigate,
}) => {
	const [isExpanded, setIsExpanded] = useState(() => {
		try {
			return localStorage.getItem(storageKey) === "true";
		} catch {
			return false;
		}
	});

	const toggleExpanded = () => {
		const next = !isExpanded;
		setIsExpanded(next);
		try {
			localStorage.setItem(storageKey, String(next));
		} catch {
			// localStorage unavailable
		}
	};

	const iconColor = isCollapsed ? "#BDD6FF" : "#0045F5";
	const hasBadge = badge !== null && badge !== undefined;

	if (isCollapsed) {
		return (
			<div className="menuleft w-full rounded-md hover:bg-white/20">
				<Tooltip label={label} placement="right" shouldWrapChildren={false}>
					<AppMenuLink
						to={collapsedLink}
						testId={testId}
						onNavigate={onNavigate}
					>
						<span className="flex w-full items-center justify-center">
							<div
								className="flex w-full items-center justify-center"
								style={{ padding: "12px 0" }}
							>
								<IconBox
									icon={icon}
									color={iconColor}
									width="24px"
									height="24px"
								/>
							</div>
						</span>
					</AppMenuLink>
				</Tooltip>
			</div>
		);
	}

	return (
		<div data-testid={testId}>
			<div
				className={`${tourName ?? ""} menuleft group py-1 overflow-hidden hover:bg-utility-blue-light-50 focus:bg-white`}
			>
				<button
					className="flex w-full items-center"
					onClick={toggleExpanded}
					data-testid={testId ? `${testId}-toggle` : undefined}
				>
					<span className="flex w-full items-center">
						<div
							className="flex shrink-0 items-center"
							style={{ paddingLeft: "24px", marginRight: "16px" }}
						>
							<IconBox
								icon={icon}
								color={iconColor}
								width="24px"
								height="24px"
							/>
						</div>
						<div
							className="flex flex-1 items-center justify-between pr-4"
							style={{ fontSize: "16px", lineHeight: "24px" }}
						>
							<span
								className="sidemuchild font-roboto group-hover:text-royal-blue group-focus:text-royal-blue"
								style={{ fontWeight: 500 }}
							>
								{label}
							</span>
							<div className="flex items-center gap-2">
								{hasBadge && (
									<div
										className="flex items-center justify-center rounded-full border border-quarternary-400 bg-gray-utility-100"
										style={{
											fontSize: "12px",
											padding: "2px 8px",
											minWidth: "28px",
											height: "22px",
										}}
									>
										<span className="font-roboto font-semibold text-secondary-700">
											{limitNotif(badge ?? 0)}
										</span>
									</div>
								)}
								<div
									className={`transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
								>
									<FiChevronDown size={16} color="#6B7280" />
								</div>
							</div>
						</div>
					</span>
				</button>
			</div>
			{isExpanded && (
				<div className="ml-[60px]">{children}</div>
			)}
		</div>
	);
};
